import { type ButtonAppearanceId, DesignGroup, resolveDesignSelection } from '@today/brand-config/runtime'
import { cva, type VariantProps } from 'class-variance-authority'
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components'
import { useOptionalDesign } from '../brand/brand-provider'
import { cn } from '../lib/cn'
import { Spinner } from '../spinner/spinner'

/**
 * Variant styles for {@link Button}.
 *
 * Every color, radius, and type-scale class resolves to a design token registered
 * via `@theme` (see `styles/theme.css` in packages/ui-primitives/src/styles/theme.css:12-95),
 * so no hardcoded color values appear here.
 *
 * All colors come from @today/tokens, resolved from the bound brand.
 *
 * @remarks
 * - Primary: brand primary colour
 * - Secondary: brand secondary colour
 * - Danger: Error red from semantic tokens
 * - Success: Success green from semantic tokens
 * - Muted: Surface/border colors for ghost-style buttons
 *
 * @example
 * ```tsx
 * const classes = buttonVariants({ variant: 'primary', size: 'md' })
 * // Returns: "inline-flex items-center ... bg-ds-primary text-ds-on-primary ..."
 * ```
 */
export const buttonVariants = cva(
	'inline-flex items-center justify-center font-normal transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:opacity-50 disabled:pointer-events-none rounded-ds-md',
	{
		variants: {
			// Filled intents get their colours from `compoundVariants` (one per appearance).
			variant: {
				primary: '',
				secondary: 'bg-ds-secondary text-ds-text hover:bg-ds-surface-elevated border border-ds-border',
				danger: '',
				success: '',
				muted: 'bg-ds-surface-muted text-ds-text hover:bg-ds-surface-elevated border border-ds-border',
			},
			// Brand-selectable (`components.button`, TO-368); secondary and muted look the same in all three.
			appearance: { solid: '', soft: '', outline: '' },
			size: {
				sm: 'text-ds-sm px-3 py-1.5 h-8',
				md: 'text-ds-base px-4 py-2 h-10',
				lg: 'text-ds-lg px-6 py-3 h-12',
			},
		},
		compoundVariants: [
			{ variant: 'primary', appearance: 'solid', class: 'bg-ds-primary text-ds-on-primary hover:bg-ds-primary-hover' },
			{ variant: 'primary', appearance: 'soft', class: 'bg-ds-primary/15 text-ds-primary hover:bg-ds-primary/25' },
			{
				variant: 'primary',
				appearance: 'outline',
				class: 'border border-ds-primary bg-transparent text-ds-primary hover:bg-ds-primary/10',
			},
			{ variant: 'danger', appearance: 'solid', class: 'bg-ds-danger text-ds-on-danger hover:opacity-90' },
			{ variant: 'danger', appearance: 'soft', class: 'bg-ds-danger/15 text-ds-danger hover:bg-ds-danger/25' },
			{
				variant: 'danger',
				appearance: 'outline',
				class: 'border border-ds-danger bg-transparent text-ds-danger hover:bg-ds-danger/10',
			},
			{ variant: 'success', appearance: 'solid', class: 'bg-ds-success text-ds-on-success hover:opacity-90' },
			{ variant: 'success', appearance: 'soft', class: 'bg-ds-success/15 text-ds-success hover:bg-ds-success/25' },
			{
				variant: 'success',
				appearance: 'outline',
				class: 'border border-ds-success bg-transparent text-ds-success hover:bg-ds-success/10',
			},
		],
		defaultVariants: { variant: 'primary', appearance: 'solid', size: 'md' },
	},
)

/**
 * Props for the {@link Button} component.
 *
 * Extends React Aria's ButtonProps with CVA variant props for styling.
 *
 * @property variant - Intent: 'primary' | 'secondary' | 'danger' | 'success' | 'muted'
 * @property appearance - 'solid' | 'soft' | 'outline'; defaults to the bound brand's `components.button`
 *   selection, or 'solid' outside a `BrandProvider` (brand-design-configuration REQ-004)
 * @property size - Button size: 'sm' (h-8) | 'md' (h-10, default) | 'lg' (h-12)
 * @property className - Optional additional Tailwind classes
 * @property children - Button label/content
 * @property onPress - Click handler (React Aria pattern)
 * @property isDisabled - Disabled state (React Aria pattern)
 * @property isLoading - Async-action-in-flight state (TO-641): blocks activation and form submit,
 *   sets `aria-busy`, and overlays a spinner on the label without changing the button's size
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onPress={() => console.log('Clicked')}>
 *   Save Changes
 * </Button>
 * ```
 */
export interface ButtonProps
	extends Omit<AriaButtonProps, 'className' | 'isPending'>,
		VariantProps<typeof buttonVariants> {
	className?: string
	isLoading?: boolean
}

type ButtonChildren = AriaButtonProps['children']
type ButtonRender = AriaButtonProps['render']
type ButtonSize = VariantProps<typeof buttonVariants>['size']

/**
 * Children of a loading button: the label kept laid out but transparent (so the button keeps its
 * width and its accessible name), with the spinner overlaid on its centre. The spinner is
 * `aria-hidden` because a `role="status"` inside a button would leak its text into the name; the
 * busy state is conveyed by `aria-busy` and React Aria's pending announcement instead.
 */
function renderLoadingChildren(children: ButtonChildren, size: ButtonSize): ButtonChildren {
	return (renderProps) => (
		<>
			<span className="inline-flex items-center justify-center gap-[inherit] opacity-0">
				{typeof children === 'function' ? children(renderProps) : children}
			</span>
			<Spinner
				aria-hidden="true"
				size={size === 'lg' ? 'md' : 'sm'}
				className="absolute inset-0 m-auto border-current/25 border-t-current"
			/>
		</>
	)
}

/**
 * React Aria drops `aria-busy` from the props it forwards to the DOM, so a loading button adds it
 * through RAC's sanctioned `render` hook, still delegating to a caller-supplied `render`.
 */
function renderBusyButton(render: ButtonRender): ButtonRender {
	return (domProps, renderProps) => {
		const busyProps = { ...domProps, 'aria-busy': true }
		return render ? render(busyProps, renderProps) : <button {...busyProps} />
	}
}

/**
 * Accessible button primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation, focus management, and screen reader support
 *   come from React Aria's `<Button>` primitive.
 * - **Styling:** All colors and spacing resolve to design tokens from `@today/tokens`
 *   (resolved per bound brand). No hardcoded values.
 * - **Variants:** Primary (brand primary colour), secondary, danger, success, muted.
 * - **Sizes:** Small (h-8), medium (h-10, default), large (h-12).
 *
 * @example Basic usage
 * ```tsx
 * import { Button } from '@today/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Button variant="primary" onPress={handleSubmit}>
 *         Submit
 *       </Button>
 *       <Button variant="muted" onPress={handleCancel}>
 *         Cancel
 *       </Button>
 *     </form>
 *   )
 * }
 * ```
 *
 * @example While an async action is in flight
 * ```tsx
 * <Button type="submit" isLoading={mutation.isPending}>
 *   Save
 * </Button>
 * ```
 *
 * @example With disabled state
 * ```tsx
 * <Button variant="primary" isDisabled={!isFormValid}>
 *   Save
 * </Button>
 * ```
 *
 * @see {@link buttonVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/button/button.stories.tsx for visual examples
 */
export function Button({
	variant,
	size,
	appearance,
	className,
	isLoading = false,
	children,
	render,
	...props
}: ButtonProps) {
	const design = useOptionalDesign()
	const resolved =
		appearance ?? (resolveDesignSelection(design, DesignGroup.Components, 'button') as ButtonAppearanceId)
	// React Aria's pending state (not `isDisabled`) blocks presses and form submit while keeping the
	// button focusable, so a keyboard user who just pressed it does not lose focus to <body>.
	return (
		<AriaButton
			{...props}
			isPending={isLoading}
			render={isLoading ? renderBusyButton(render) : render}
			data-appearance={resolved}
			className={cn(buttonVariants({ variant, appearance: resolved, size }), isLoading && 'relative', className)}
		>
			{isLoading ? renderLoadingChildren(children, size) : children}
		</AriaButton>
	)
}
